import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { motion } from "framer-motion";
import TilesBackground from "../assets/Tiles.png";
import { useAuth } from "../contexts/AuthContext";
import { Menu, X } from "lucide-react";

const LandingPage = () => {
  const [isHovered, setIsHovered] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isAuthenticated, user, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLoginClick = () => {
    navigate("/login");
  };
  
  const handleDashboardClick = () => {
    navigate("/");
  };

  const handleGetStartedClick = () => {
    if (isAuthenticated) {
      navigate("/");
    } else {
      navigate("/login");
    }
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <div className="min-h-screen bg-[#fbfbfd] dark:bg-[#1d1d1f] text-[#1d1d1f] dark:text-[#f5f5f7]">
      {/* Navigation - Apple style with translucent background when scrolled */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-[#fbfbfd]/90 dark:bg-[#1d1d1f]/90 backdrop-blur-md shadow-sm" : ""
      }`}>
        <div className="container mx-auto py-4 px-4 sm:px-6 flex items-center justify-between">
          <div className="flex items-center">
            <span className="text-xl font-medium tracking-tight">
              GENAICore
            </span>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-7">
            <a className="text-sm font-medium hover:text-[#168aad] dark:hover:text-[#52b69a] transition-colors" href="#features">
              Features
            </a>
            <a className="text-sm font-medium hover:text-[#168aad] dark:hover:text-[#52b69a] transition-colors" href="#providers">
              Providers
            </a>
            <a className="text-sm font-medium hover:text-[#168aad] dark:hover:text-[#52b69a] transition-colors" href="#integration">
              Integration
            </a>
            <a className="text-sm font-medium hover:text-[#168aad] dark:hover:text-[#52b69a] transition-colors" href="#pricing">
              Pricing
            </a>
          </div>

          {/* Mobile menu button */}
          <button 
            className="md:hidden flex items-center" 
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>

          <div className="hidden md:flex items-center space-x-4">
            {isLoading ? (
              <div className="h-9 w-24 bg-[#f5f5f7] dark:bg-[#252527] animate-pulse rounded-full"></div>
            ) : isAuthenticated ? (
              <>
                <span className="text-sm font-medium hidden md:inline-block">
                  Welcome, {user?.username || 'User'}
                </span>
                <Button 
                  variant="ghost" 
                  className="hidden sm:flex text-sm font-medium hover:text-[#168aad] dark:hover:text-[#52b69a] transition-colors"
                  onClick={handleDashboardClick}
                >
                  Dashboard
                </Button>
              </>
            ) : (
              <Button 
                variant="ghost" 
                className="hidden sm:flex text-sm font-medium hover:text-[#168aad] dark:hover:text-[#52b69a] transition-colors"
                onClick={handleLoginClick}
              >
                Log in
              </Button>
            )}
            <Button 
              className="bg-[#168aad] hover:bg-[#1a759f] text-white rounded-full px-6 py-1.5 text-sm font-medium transition-colors"
              onClick={handleGetStartedClick}
            >
              {isAuthenticated ? "Go to Dashboard" : "Request a demo"}
            </Button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-[#fbfbfd] dark:bg-[#1d1d1f] border-t border-[#e5e5e7] dark:border-[#2d2d2f]">
            <div className="px-4 pt-2 pb-4 space-y-1">
              <a 
                href="#features" 
                className="block py-2 text-base font-medium hover:text-[#168aad] dark:hover:text-[#52b69a]"
                onClick={() => setMobileMenuOpen(false)}
              >
                Features
              </a>
              <a 
                href="#providers" 
                className="block py-2 text-base font-medium hover:text-[#168aad] dark:hover:text-[#52b69a]"
                onClick={() => setMobileMenuOpen(false)}
              >
                Providers
              </a>
              <a 
                href="#integration" 
                className="block py-2 text-base font-medium hover:text-[#168aad] dark:hover:text-[#52b69a]"
                onClick={() => setMobileMenuOpen(false)}
              >
                Integration
              </a>
              <a 
                href="#pricing" 
                className="block py-2 text-base font-medium hover:text-[#168aad] dark:hover:text-[#52b69a]"
                onClick={() => setMobileMenuOpen(false)}
              >
                Pricing
              </a>
              
              <div className="pt-4 flex flex-col space-y-2">
                {isAuthenticated ? (
                  <>
                    <span className="text-sm font-medium">
                      Welcome, {user?.username || 'User'}
                    </span>
                    <Button 
                      variant="ghost" 
                      className="justify-start px-0 text-sm font-medium hover:text-[#168aad] dark:hover:text-[#52b69a]"
                      onClick={() => {
                        handleDashboardClick();
                        setMobileMenuOpen(false);
                      }}
                    >
                      Dashboard
                    </Button>
                  </>
                ) : (
                  <Button 
                    variant="ghost" 
                    className="justify-start px-0 text-sm font-medium hover:text-[#168aad] dark:hover:text-[#52b69a]"
                    onClick={() => {
                      handleLoginClick();
                      setMobileMenuOpen(false);
                    }}
                  >
                    Log in
                  </Button>
                )}
                <Button 
                  className="bg-[#168aad] hover:bg-[#1a759f] text-white rounded-full py-1.5 text-sm font-medium transition-colors w-full mt-2"
                  onClick={() => {
                    handleGetStartedClick();
                    setMobileMenuOpen(false);
                  }}
                >
                  {isAuthenticated ? "Go to Dashboard" : "Request a demo"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section - Apple style with centered content and lots of whitespace */}
      <section className="container mx-auto pt-32 pb-20 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto text-center">
          <motion.h1 
            className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight mb-6 leading-tight"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            The unified interface for 
            <span className="block text-[#168aad] dark:text-[#52b69a]">
              all your AI models
            </span>
          </motion.h1>
          
          <motion.p 
            className="text-base sm:text-lg md:text-xl text-[#86868b] dark:text-[#a1a1a6] mb-10 max-w-2xl mx-auto font-normal leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            GenAICore provides a single API to access OpenAI, Anthropic, Gemini, and more—with built-in authentication, usage tracking, and smart request routing.
          </motion.p>
          
          <motion.div 
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <Button 
              size="lg" 
              className="bg-[#168aad] hover:bg-[#1a759f] text-white rounded-full px-8 py-2 h-12 text-sm font-medium transition-colors w-full sm:w-auto"
              onClick={handleGetStartedClick}
            >
              {isAuthenticated ? "Go to Dashboard" : "Get started for free"}
            </Button>
            <Button 
              size="lg" 
              variant="ghost" 
              className="text-[#168aad] dark:text-[#52b69a] hover:bg-transparent hover:text-[#1a759f] dark:hover:text-[#76c893] h-12 px-8 text-sm font-medium transition-colors w-full sm:w-auto"
              onClick={() => window.location.href = "#features"}
            >
              View documentation
            </Button>
          </motion.div>
        </div>

        {/* Product Showcase - Minimalist Apple-style mockup */}
        <motion.div 
          className="mt-16 sm:mt-24 relative rounded-2xl overflow-hidden bg-white dark:bg-[#151516] shadow-xl mx-auto max-w-5xl"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <div className="aspect-video w-full relative">
            {/* Mockup UI */}
            <div className="absolute inset-0">
              {/* Top Navigation Bar */}
              <div className="h-8 w-full bg-[#f5f5f7] dark:bg-[#252527] flex items-center px-3 border-b border-[#e5e5e7] dark:border-[#2d2d2f]">
                <div className="flex space-x-2">
                  <div className="w-3 h-3 rounded-full bg-[#ff5f57]"></div>
                  <div className="w-3 h-3 rounded-full bg-[#febc2e]"></div>
                  <div className="w-3 h-3 rounded-full bg-[#28c840]"></div>
                </div>
                <div className="flex-1 flex justify-center">
                  <div className="h-5 w-64 bg-[#e5e5e7] dark:bg-[#252527] rounded-full"></div>
                </div>
              </div>
              
              {/* App Content */}
              <div className="grid grid-cols-5 h-[calc(100%-2rem)]">
                {/* Sidebar */}
                <div className="col-span-1 bg-[#f5f5f7] dark:bg-[#252527] border-r border-[#e5e5e7] dark:border-[#2d2d2f] p-3">
                  <div className="space-y-3">
                    <div className="h-8 bg-[#168aad] dark:bg-[#1a759f] rounded-md flex items-center justify-start px-3">
                      <div className="w-3 h-3 rounded-full bg-white mr-2"></div>
                      <div className="h-3 w-16 bg-white/30 rounded-sm"></div>
                    </div>
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div key={i} className="h-8 bg-[#e5e5e7] dark:bg-[#333336] rounded-md"></div>
                    ))}
                  </div>
                </div>
                
                {/* Main Content */}
                <div className="col-span-4 p-6 bg-white dark:bg-[#1d1d1f] overflow-y-auto">
                  <div className="mb-6">
                    <div className="h-8 w-48 bg-[#f5f5f7] dark:bg-[#252527] rounded-lg mb-4"></div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 rounded-xl bg-[#f5f5f7] dark:bg-[#252527]">
                        <div className="h-5 w-24 mb-3 bg-[#e5e5e7] dark:bg-[#333336] rounded-md"></div>
                        <div className="space-y-2">
                          <div className="h-3 w-full bg-[#e5e5e7] dark:bg-[#333336] rounded-md"></div>
                          <div className="h-3 w-5/6 bg-[#e5e5e7] dark:bg-[#333336] rounded-md"></div>
                          <div className="h-3 w-4/6 bg-[#e5e5e7] dark:bg-[#333336] rounded-md"></div>
                        </div>
                      </div>
                      <div className="p-4 rounded-xl bg-[#f5f5f7] dark:bg-[#252527]">
                        <div className="h-5 w-24 mb-3 bg-[#e5e5e7] dark:bg-[#333336] rounded-md"></div>
                        <div className="space-y-2">
                          <div className="h-3 w-full bg-[#e5e5e7] dark:bg-[#333336] rounded-md"></div>
                          <div className="h-3 w-5/6 bg-[#e5e5e7] dark:bg-[#333336] rounded-md"></div>
                          <div className="h-3 w-4/6 bg-[#e5e5e7] dark:bg-[#333336] rounded-md"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mb-6">
                    <div className="h-8 w-64 bg-[#f5f5f7] dark:bg-[#252527] rounded-lg mb-4"></div>
                    <div className="p-4 bg-[#f5f5f7] dark:bg-[#252527] rounded-xl">
                      <div className="h-6 w-full bg-[#e5e5e7] dark:bg-[#333336] rounded-md mb-2"></div>
                      <div className="h-6 w-5/6 bg-[#e5e5e7] dark:bg-[#333336] rounded-md mb-2"></div>
                      <div className="h-6 w-4/6 bg-[#e5e5e7] dark:bg-[#333336] rounded-md"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Providers Section - Apple-style grid layout */}
      <section className="container mx-auto py-16 sm:py-20 px-4 sm:px-6">
        <h2 className="text-center text-base font-medium text-[#86868b] dark:text-[#a1a1a6] mb-8 sm:mb-10">
          Integrated with leading AI providers
        </h2>
        
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:flex lg:flex-wrap justify-center items-center gap-8 lg:gap-20">
          {[
            { name: "OpenAI", logo: "bg-[#10a37f]" },
            { name: "Anthropic", logo: "bg-[#b028ff]" },
            { name: "Google", logo: "bg-[#4285f4]" },
            { name: "Mistral", logo: "bg-[#0066ff]" },
            { name: "Cohere", logo: "bg-[#6945ff]" }
          ].map((company, i) => (
            <div key={i} className="text-center flex justify-center">
              <div className="flex items-center">
                <div className={`w-6 h-6 rounded-md mr-2 ${company.logo}`}></div>
                <span className="text-[#1d1d1f] dark:text-[#f5f5f7] font-medium text-sm">{company.name}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Features Section - Apple-style cards */}
      <section id="features" className="container mx-auto py-16 sm:py-20 px-4 sm:px-6">
        <h2 className="text-center text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight mb-12 sm:mb-20">
          All your AI models <span className="block">through a single API</span>
        </h2>
        
        <div className="grid md:grid-cols-2 gap-8 items-start">
          {/* Unified API Block */}
          <motion.div 
            className="bg-white dark:bg-[#151516] rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition-shadow h-full flex flex-col justify-between"
            whileHover={{ y: -5 }}
          >
            <div>
              <h3 className="text-xl font-medium mb-4">Unified API</h3>
              <p className="text-[#86868b] dark:text-[#a1a1a6] mb-6 text-base leading-relaxed">
                Access OpenAI, Anthropic, Google, Mistral, and more through a single, consistent API. No need to manage multiple SDKs and authentication flows.
              </p>
              <div className="bg-[#f5f5f7] dark:bg-[#252527] rounded-lg p-3 sm:p-4 mb-6 font-mono text-sm overflow-auto">
                <pre className="text-[#168aad] dark:text-[#52b69a] text-xs sm:text-sm">POST api.genaicore.com/api/completions</pre>
                <pre className="text-[#00ba7c] dark:text-[#2ed477] text-xs sm:text-sm">{'{\n  "model": "gpt-4",\n  "provider": "openai",\n  "messages": [...]\n}'}</pre>
              </div>
            </div>
            <a href="#" className="text-sm font-medium text-[#168aad] dark:text-[#52b69a] hover:text-[#1a759f] dark:hover:text-[#76c893] flex items-center group">
              View API documentation 
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </a>
          </motion.div>
          
          {/* Management Dashboard Block */}
          <motion.div 
            className="bg-white dark:bg-[#151516] rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition-shadow h-full flex flex-col justify-between"
            whileHover={{ y: -5 }}
          >
            <div>
              <h3 className="text-xl font-medium mb-4">Smart Management</h3>
              <p className="text-[#86868b] dark:text-[#a1a1a6] mb-6 text-base leading-relaxed">
                Monitor usage, control costs, and analyze performance across all your AI providers through a comprehensive dashboard.
              </p>
              <div className="flex flex-col space-y-4 mb-6">
                <div className="bg-[#f5f5f7] dark:bg-[#252527] p-3 sm:p-4 rounded-lg">
                  <h4 className="font-medium text-[#1d1d1f] dark:text-white mb-2 text-sm">Usage Analytics</h4>
                  <p className="text-sm text-[#86868b] dark:text-[#a1a1a6]">Real-time metrics on token usage and request volumes.</p>
                </div>
                <div className="bg-[#f5f5f7] dark:bg-[#252527] p-3 sm:p-4 rounded-lg">
                  <h4 className="font-medium text-[#1d1d1f] dark:text-white mb-2 text-sm">Cost Management</h4>
                  <p className="text-sm text-[#86868b] dark:text-[#a1a1a6]">Set budgets and receive alerts when nearing limits.</p>
                </div>
              </div>
            </div>
            <a href="#" className="text-sm font-medium text-[#168aad] dark:text-[#52b69a] hover:text-[#1a759f] dark:hover:text-[#76c893] flex items-center group">
              Explore dashboard
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Solutions Grid - Apple style grid layout */}
      <section id="providers" className="container mx-auto py-16 sm:py-20 px-4 sm:px-6">
        <h2 className="text-center text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight mb-4 sm:mb-8">
          Solutions for every need
        </h2>
        <p className="text-center text-[#86868b] dark:text-[#a1a1a6] max-w-2xl mx-auto mb-10 sm:mb-16 text-base">
          GenAICore offers flexible solutions to fit any project requirement, from startups to enterprise deployments.
        </p>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {[
            {
              title: "API Access",
              description: "Simple REST API access to multiple models with a unified interface.",
              icon: "M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
            },
            {
              title: "Cost Optimization",
              description: "Automatically route requests to the best-performing model for your budget.",
              icon: "M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            },
            {
              title: "Analytics",
              description: "Comprehensive metrics and insights for all your AI operations.",
              icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            },
            {
              title: "Fine-tuning",
              description: "Simplify the process of fine-tuning models with your custom data.",
              icon: "M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z"
            },
            {
              title: "Team Management",
              description: "Organize users, manage permissions, and control access to resources.",
              icon: "M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
            },
            {
              title: "Enterprise Security",
              description: "Advanced security features for enterprise deployments.",
              icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
            }
          ].map((solution, i) => (
            <motion.div 
              key={i} 
              className="bg-white dark:bg-[#151516] rounded-2xl p-5 sm:p-6 shadow-md hover:shadow-lg transition-all"
              whileHover={{ y: -5 }}
            >
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#f5f5f7] dark:bg-[#252527] rounded-full flex items-center justify-center mb-4">
                <svg className="h-5 w-5 sm:h-6 sm:w-6 text-[#168aad] dark:text-[#52b69a]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={solution.icon} />
                </svg>
              </div>
              <h3 className="text-lg font-medium mb-2">{solution.title}</h3>
              <p className="text-[#86868b] dark:text-[#a1a1a6] text-sm">{solution.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section - Apple style simplicity */}
      <section id="integration" className="container mx-auto py-16 sm:py-20 px-4 sm:px-6">
        <div className="bg-[#f5f5f7] dark:bg-[#252527] rounded-3xl p-8 sm:p-12 md:p-16 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold mb-4 sm:mb-6">Ready to get started?</h2>
          <p className="text-[#86868b] dark:text-[#a1a1a6] max-w-2xl mx-auto mb-8 sm:mb-10 text-base sm:text-lg">
            Join thousands of developers already using GenAICore to power their AI applications.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              size="lg" 
              className="bg-[#168aad] hover:bg-[#1a759f] text-white rounded-full px-8 py-2 h-12 text-sm font-medium transition-colors w-full sm:w-auto"
              onClick={handleGetStartedClick}
            >
              {isAuthenticated ? "Go to Dashboard" : "Get started for free"}
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="text-[#168aad] dark:text-[#52b69a] hover:bg-transparent hover:text-[#1a759f] dark:hover:text-[#76c893] h-12 px-8 text-sm border-[#168aad] dark:border-[#52b69a] rounded-full font-medium transition-colors w-full sm:w-auto"
              onClick={() => window.open('mailto:sales@genaicore.com')}
            >
              Contact sales
            </Button>
          </div>
        </div>
      </section>

      {/* Footer - Simplified Apple style footer */}
      <footer className="bg-[#f5f5f7] dark:bg-[#151516] py-10 sm:py-12 mt-10 sm:mt-12 px-4 sm:px-6">
        <div className="container mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-8 mb-8">
            <div>
              <h3 className="text-sm font-medium mb-4">Platform</h3>
              <ul className="space-y-2">
                <li><a href="#features" className="text-xs text-[#86868b] dark:text-[#a1a1a6] hover:text-[#168aad] dark:hover:text-[#52b69a]">Features</a></li>
                <li><a href="#providers" className="text-xs text-[#86868b] dark:text-[#a1a1a6] hover:text-[#168aad] dark:hover:text-[#52b69a]">Providers</a></li>
                <li><a href="#integration" className="text-xs text-[#86868b] dark:text-[#a1a1a6] hover:text-[#168aad] dark:hover:text-[#52b69a]">Integration</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-medium mb-4">Documentation</h3>
              <ul className="space-y-2">
                <li><Link to="/docs" className="text-xs text-[#86868b] dark:text-[#a1a1a6] hover:text-[#168aad] dark:hover:text-[#52b69a]">Documentation Hub</Link></li>
                <li><Link to="/docs/platform-overview" className="text-xs text-[#86868b] dark:text-[#a1a1a6] hover:text-[#168aad] dark:hover:text-[#52b69a]">Platform Overview</Link></li>
                <li><Link to="/docs/get-started/introduction" className="text-xs text-[#86868b] dark:text-[#a1a1a6] hover:text-[#168aad] dark:hover:text-[#52b69a]">Getting Started</Link></li>
              </ul>
            </div>
            <div className="col-span-2 md:col-span-1">
              <h3 className="text-sm font-medium mb-4">Legal</h3>
              <ul className="space-y-2">
                <li><a href="#" className="text-xs text-[#86868b] dark:text-[#a1a1a6] hover:text-[#168aad] dark:hover:text-[#52b69a]">Terms of Service</a></li>
                <li><a href="#" className="text-xs text-[#86868b] dark:text-[#a1a1a6] hover:text-[#168aad] dark:hover:text-[#52b69a]">Privacy Policy</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-[#d2d2d7] dark:border-[#424245] pt-6 sm:pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-xs text-[#86868b] dark:text-[#a1a1a6] mb-4 md:mb-0 text-center md:text-left">
              &copy; {new Date().getFullYear()} GenAICore. All rights reserved.
            </p>
            <div className="flex space-x-6">
              <a href="#" className="text-[#86868b] dark:text-[#a1a1a6] hover:text-[#168aad] dark:hover:text-[#52b69a]">
                <span className="sr-only">GitHub</span>
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage; 